import { type JSX, useState } from "react";

import * as MuiIcons from "@mui/icons-material";
import { SvgIconComponent } from "@mui/icons-material";
import {
  useCsCfgObject,
  useCsConfigurationApi,
} from "@plansoft-configuration-renderer/framework";

import { ButtonUi } from "./ButtonUi";
import { CustomButtonUi } from "./CustomButtonUi";
import {
  UserDefinedFunctionButtonComponentMapping,
  UserDefinedFunctionButtonOverridableProps,
} from "./UserDefinedFunctionButtonProps";

type IconsType = typeof MuiIcons;
type IconNames = keyof IconsType;

const DEFAULT_COMPONENT_MAPPING: UserDefinedFunctionButtonComponentMapping = {
  ButtonUi,
};

export function CustomUserDefinedFunctionButton(
  props: UserDefinedFunctionButtonOverridableProps,
): JSX.Element {
  const [disabled, setDisabled] = useState(false);
  const {
    functionName,
    input,
    components: Components = DEFAULT_COMPONENT_MAPPING,
    custom,
    ...otherProps
  } = props;
  const { invokeUserDefinedFunction } = useCsConfigurationApi();
  const { path } = useCsCfgObject();

  async function handleClick(): Promise<void> {
    try {
      setDisabled(true);
      const output = await invokeUserDefinedFunction({
        name: functionName,
        input,
        path,
      });
      console.log(`invokeUserDefinedFunction "${functionName}":`, {
        input,
        output,
      });
    } catch (err) {
      console.log(err);
    } finally {
      setDisabled(false);
    }
  }

  const buttonType = custom?.buttonType;
  const iconName = custom?.buttonIcon;
  const iconPosition = custom?.buttonIconPosition || "start";
  let Icon: SvgIconComponent | undefined;

  if (iconName && typeof iconName === "string" && iconName in MuiIcons) {
    Icon = MuiIcons[iconName as IconNames] as SvgIconComponent;
  }

  if (Icon && buttonType) {
    switch (buttonType) {
      case "icon":
        return (
          <CustomButtonUi
            {...otherProps}
            onClick={handleClick}
            disabled={disabled}
            icon={Icon}
            hybridMode={false}
          />
        );

      case "hybrid":
        return (
          <CustomButtonUi
            {...otherProps}
            onClick={handleClick}
            disabled={disabled}
            icon={Icon}
            hybridMode={true}
            iconPosition={iconPosition}
          />
        );
    }
  }

  return (
    <Components.ButtonUi
      {...otherProps}
      onClick={handleClick}
      disabled={disabled}
    />
  );
}
